/**
 * The narrative fields AI Dictate can target (PD-1516), keyed by their stable
 * `data-testid`s so focus tracking is pure event delegation — no per-field
 * wiring in the section components. Structured pickers (codes, meds, vitals)
 * are deliberately absent: dictation must never write coded content.
 */

export type DictationField = {
  /** RHF path — index placeholders resolved from the matched testid. */
  path: string
  /** Human label for the recording bar and the formatting prompt. */
  label: string
}

const EXACT: Record<string, DictationField> = {
  'subjective-chief-complaint': { path: 'summary.chiefComplaint', label: 'Chief Complaint' },
  'assessment-clinical-impression': { path: 'summary.assessment', label: 'Assessment' },
  'plan-narrative': { path: 'summary.plan', label: 'Plan' },
  'mdm-narrative': { path: 'summary.mdm', label: 'Medical Decision Making' },
}

const HPI_PATTERN = /^subjective-hpi-(\d+)$/

export function resolveDictationField(element: Element | null): DictationField | null {
  if (!element) return null
  const testId = element.getAttribute('data-testid')
  if (!testId) return null
  const exact = EXACT[testId]
  if (exact) return exact
  const hpi = HPI_PATTERN.exec(testId)
  if (hpi) {
    return { path: `summary.hpi.${hpi[1]}.text`, label: 'History of Present Illness' }
  }
  return null
}

export type DictationSelection = { start: number; end: number; text: string }

/** The element's current selection, or null when it's collapsed (a caret). */
export function readDictationSelection(element: Element): DictationSelection | null {
  const control = element as HTMLTextAreaElement
  if (typeof control.selectionStart !== 'number' || typeof control.selectionEnd !== 'number') {
    return null
  }
  const { selectionStart, selectionEnd, value } = control
  if (selectionStart === selectionEnd) return null
  return {
    start: selectionStart,
    end: selectionEnd,
    text: value.slice(selectionStart, selectionEnd),
  }
}
